import { z } from 'zod';
import { timestampSchema, paginationResponseSchema } from './common.schemas';

/**
 * Escalation System Schemas
 */

// Escalation schema
export const escalationSchema = z
  .object({
    id: z.string().uuid(),
    reason: z.string(),
    priority: z.enum(['low', 'normal', 'high', 'urgent']),
    status: z.enum(['new', 'assigned', 'in_progress', 'resolved']),
    aiSummary: z.string(),
    customerContext: z.record(z.unknown()).nullable(),
    assignedAt: z.coerce.date().nullable(),
    resolvedAt: z.coerce.date().nullable(),
    resolution: z.string().nullable(),
    conversationId: z.string().uuid(),
    organizationId: z.string().uuid(),
    assignedToId: z.string().uuid().nullable(),
    assignedTo: z
      .object({
        id: z.string().uuid(),
        name: z.string(),
        email: z.string().email(),
      })
      .nullable(),
  })
  .merge(timestampSchema);

// Create escalation (typically created automatically by system)
export const createEscalationSchema = z.object({
  conversationId: z.string().uuid(),
  reason: z.string().min(1),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).default('normal'),
  aiSummary: z.string(),
  customerContext: z.record(z.unknown()).optional(),
});

// Update escalation
export const updateEscalationSchema = z.object({
  status: z.enum(['new', 'assigned', 'in_progress', 'resolved']).optional(),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
  assignedToId: z.string().uuid().nullable().optional(),
  resolution: z.string().optional(),
});

// Escalation list response
export const escalationListResponseSchema = z.object({
  escalations: z.array(escalationSchema),
  pagination: paginationResponseSchema,
});

// Escalation filters
export const escalationFiltersSchema = z.object({
  status: z.enum(['new', 'assigned', 'in_progress', 'resolved']).optional(),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
  assignedToId: z.string().uuid().optional(),
  fromDate: z.coerce.date().optional(),
  toDate: z.coerce.date().optional(),
});

// Escalation assignment
export const assignEscalationSchema = z.object({
  userId: z.string().uuid(),
});

// Export inferred types
export type Escalation = z.infer<typeof escalationSchema>;
export type CreateEscalationInput = z.infer<typeof createEscalationSchema>;
export type UpdateEscalationInput = z.infer<typeof updateEscalationSchema>;
export type EscalationListResponse = z.infer<typeof escalationListResponseSchema>;
export type EscalationFilters = z.infer<typeof escalationFiltersSchema>;
export type AssignEscalationInput = z.infer<typeof assignEscalationSchema>;
